/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
'use client';
import {
  Alert,
  Anchor,
  Button,
  Container,
  Divider,
  Group,
  Stack,
  TextInput,
  Text,
  Title,
  LoadingOverlay,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSupabase } from '@shady-pines/api';
import { isNonNil } from '@shady-pines/types';
import { IconBrandGoogle, IconBrandApple, IconUserPlus } from '@tabler/icons-react';
import { isNil } from 'lodash';
import { useRouter, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
//
const OAuthProvider = ['google', 'apple'] as const;
type OAuthProvider = (typeof OAuthProvider)[number];

export const Login = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = useSupabase();
  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
  });
  const [error, setError] = useState<ReactNode>();
  const [warning, setWarning] = useState<ReactNode>();
  const session = searchParams.get('session');

  useEffect(() => {
    if (isNil(session)) {
      return;
    }

    const runLogic = async () => {
      const decoded = atob(session);

      const parsedSession = JSON.parse(decoded);
      if (parsedSession && parsedSession.user.is_anonymous) {
        return;
      }
      const redirectTo = searchParams.get('redirectTo') || '/shows/me';
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const sessionResult = await supabase.auth.setSession(parsedSession);
      if (isNonNil(sessionResult.error) || isNil(sessionResult.data.session)) {
        return;
      }

      window.location.href = redirectTo;
    };
    void runLogic();
  }, [router, searchParams, session, supabase.auth]);

  const handleSubmit = useCallback(
    async (values: typeof form.values) => {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailOrUserName: values.email,
          password: values.password,
        }),
      });

      const body = await response.json();
      if (body.status === 'success') {
        setError(undefined);
        setWarning(undefined);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await supabase.auth.setSession(body.session);
        const redirectTo = searchParams.get('redirectTo') || '/shows/me';
        window.location.href = redirectTo;
      } else if (body.status === 'error') {
        console.error('Sign in failed', body.error);
        setError(
          <Text>
            Sign in failed. If your account was previously associated with a social login, try
            logging in with that instead. If the issue persists, please file a bug{' '}
            <Anchor href="https://shady-pines-radio.vercel.app/bug-reports/new" target="_blank">
              here.
            </Anchor>
            {`\n\nAlternatively, if you haven't signed up yet, you can create an account below`}
          </Text>,
        );
      } else if (body.status === 'password-reset-required') {
        setWarning(
          <Text>
            Your account requires a password reset. Please check your email for instructions.
          </Text>,
        );
      }
    },
    [form, searchParams, supabase.auth],
  );

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    const redirectTo = searchParams.get('redirectTo') ?? '/shows/me';
    const callbackUrl = new URL('/auth/callback', window.location.origin);
    callbackUrl.searchParams.set('redirectTo', redirectTo);
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: callbackUrl.toString(),
        queryParams: { prompt: 'select_account' },
      },
    });
  };

  return (
    <Container size="xs" py="xl">
      <Stack gap="lg">
        {form.submitting && <LoadingOverlay />}

        <Title order={2}>Login</Title>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Email or username"
              placeholder="your@email.com"
              {...form.getInputProps('email')}
            />
            <TextInput
              type="password"
              label="Password"
              placeholder="Your password"
              {...form.getInputProps('password')}
            />
            <Anchor
              component="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                router.push('/account/forgot-password');
              }}
            >
              Forgot your password?
            </Anchor>
            <Button
              variant="primary"
              type="submit"
              disabled={form.submitting}
              loading={form.submitting}
              fullWidth
            >
              Sign in with Email or Username
            </Button>
            {isNonNil(error) && (
              <Alert color="red" title="Sign in failed">
                {error}
              </Alert>
            )}
            {isNonNil(warning) && (
              <Alert color="yellow" title="Password reset required">
                {warning}
              </Alert>
            )}
          </Stack>
        </form>

        <Divider label="Or continue with" labelPosition="center" />
        <Group grow>
          <Button
            disabled={form.submitting}
            variant="default"
            leftSection={<IconBrandGoogle size={16} />}
            onClick={() => void handleOAuthSignIn('google')}
          >
            Google
          </Button>

          <Button
            disabled={form.submitting}
            variant="default"
            leftSection={<IconBrandApple size={16} />}
            onClick={() => void handleOAuthSignIn('apple')}
          >
            Apple
          </Button>
        </Group>

        <Divider label="Or create your account here!" labelPosition="center" />
        <Button
          variant="default"
          disabled={form.submitting}
          leftSection={<IconUserPlus size={16} />}
          onClick={() => router.replace(`/account/sign-up?email=${form.getValues().email}`)}
        >
          Create your account
        </Button>
      </Stack>
    </Container>
  );
};
